import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Task, Difficulty, RecurrencePattern } from '@/types'
import { DIFFICULTY_XP } from '@/types'
import { queueSync, getCurrentUserId, generateUUID } from '@/lib/sync'

interface TaskState {
  tasks: Task[]
  lastRecurringCheck: string | null

  // Actions
  addTask: (task: Omit<Task, 'id' | 'userId' | 'completed' | 'completedAt' | 'createdAt'>) => Task
  updateTask: (id: string, updates: Partial<Task>) => void
  deleteTask: (id: string) => void
  completeTask: (id: string) => Task | null
  uncompleteTask: (id: string) => void
  getTodaysTasks: () => Task[]
  getCompletedTasks: () => Task[]
  getPendingTasks: () => Task[]
  getRecurringTasks: () => Task[]
  resetRecurringTasks: () => void
  checkAndResetRecurring: () => void
}

const generateId = generateUUID

const calculateXpReward = (difficulty: Difficulty): number => {
  const range = DIFFICULTY_XP[difficulty]
  return Math.floor(Math.random() * (range.max - range.min + 1)) + range.min
}

const shouldResetToday = (pattern: RecurrencePattern): boolean => {
  const now = new Date()
  const today = now.getDay() // 0-6 (Sun-Sat)
  const dateOfMonth = now.getDate() // 1-31
  const month = now.getMonth() // 0-11

  switch (pattern.type) {
    case 'daily':
      return true

    case 'weekly':
      return pattern.days ? pattern.days.includes(today) : false

    case 'monthly':
      // Reset on the specified day of the month
      return pattern.dayOfMonth === dateOfMonth

    case 'yearly':
      // Reset on the specified day of the specified month
      return pattern.dayOfMonth === dateOfMonth && pattern.month === month

    default:
      return false
  }
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      tasks: [],
      lastRecurringCheck: null,

      addTask: (taskData) => {
        const newTask: Task = {
          id: generateId(),
          userId: getCurrentUserId(),
          completed: false,
          completedAt: null,
          createdAt: new Date().toISOString(),
          ...taskData,
          xpReward: taskData.xpReward || calculateXpReward(taskData.difficulty),
        }

        set((state) => ({ tasks: [...state.tasks, newTask] }))

        // Queue for Supabase sync
        queueSync('tasks', 'create', newTask.id, {
          user_id: newTask.userId,
          title: newTask.title,
          description: newTask.description,
          difficulty: newTask.difficulty,
          category: newTask.category,
          xp_reward: newTask.xpReward,
          due_date: newTask.dueDate,
          due_time: newTask.dueTime,
          is_recurring: newTask.isRecurring,
          recurrence_pattern: newTask.recurrencePattern,
          completed: newTask.completed,
          completed_at: newTask.completedAt,
          created_at: newTask.createdAt,
        })

        return newTask
      },

      updateTask: (id, updates) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return

        const updatedTask = { ...task, ...updates }

        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === id ? updatedTask : t)),
        }))

        // Queue for Supabase sync
        queueSync('tasks', 'update', id, {
          user_id: updatedTask.userId,
          title: updatedTask.title,
          description: updatedTask.description,
          difficulty: updatedTask.difficulty,
          category: updatedTask.category,
          xp_reward: updatedTask.xpReward,
          due_date: updatedTask.dueDate,
          due_time: updatedTask.dueTime,
          is_recurring: updatedTask.isRecurring,
          recurrence_pattern: updatedTask.recurrencePattern,
          completed: updatedTask.completed,
          completed_at: updatedTask.completedAt,
          created_at: updatedTask.createdAt,
        })
      },

      deleteTask: (id) => {
        set((state) => ({
          tasks: state.tasks.filter((task) => task.id !== id),
        }))

        // Queue for Supabase sync
        queueSync('tasks', 'delete', id, {})
      },

      completeTask: (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task || task.completed) return null

        const completedTask = {
          ...task,
          completed: true,
          completedAt: new Date().toISOString(),
        }

        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === id ? completedTask : t)),
        }))

        // Queue for Supabase sync (include user_id for RLS)
        queueSync('tasks', 'update', id, {
          user_id: completedTask.userId,
          completed: true,
          completed_at: completedTask.completedAt,
        })

        return completedTask
      },

      uncompleteTask: (id) => {
        set((state) => ({
          tasks: state.tasks.map((task) =>
            task.id === id
              ? { ...task, completed: false, completedAt: null }
              : task
          ),
        }))

        // Queue for Supabase sync (include user_id for RLS)
        const task = get().tasks.find((t) => t.id === id)
        queueSync('tasks', 'update', id, {
          user_id: task?.userId,
          completed: false,
          completed_at: null,
        })
      },

      getTodaysTasks: () => {
        const today = new Date().toISOString().split('T')[0]
        return get().tasks.filter((task) => {
          if (task.completed) return false
          if (!task.dueDate) return true
          return task.dueDate.split('T')[0] <= today
        })
      },

      getCompletedTasks: () => get().tasks.filter((task) => task.completed),

      getPendingTasks: () => get().tasks.filter((task) => !task.completed),

      getRecurringTasks: () => get().tasks.filter((task) => task.isRecurring),

      resetRecurringTasks: () => {
        const today = new Date().toISOString().split('T')[0]

        set((state) => ({
          tasks: state.tasks.map((task) => {
            // Only reset recurring tasks that were completed
            if (!task.isRecurring || !task.recurrencePattern || !task.completed) {
              return task
            }

            // Check if this task should reset today
            if (shouldResetToday(task.recurrencePattern)) {
              // Check if it was completed before today
              const completedDate = task.completedAt?.split('T')[0]
              if (completedDate && completedDate < today) {
                return {
                  ...task,
                  completed: false,
                  completedAt: null,
                  dueDate: new Date().toISOString(), // Due today
                }
              }
            }

            return task
          }),
          lastRecurringCheck: today,
        }))
      },

      checkAndResetRecurring: () => {
        const today = new Date().toISOString().split('T')[0]
        const { lastRecurringCheck, resetRecurringTasks } = get()

        // Only check once per day
        if (lastRecurringCheck !== today) {
          resetRecurringTasks()
        }
      },
    }),
    {
      name: 'waifudo-tasks',
    }
  )
)
